import { useCallback, useEffect, useState } from "react";
import { FormComponent } from "../../component/form/FormComponent";
import TableComponent from "../../component/form/TableComponent";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../context/notificationContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as TeamAPI from "../../api/team";
import { Field } from "../../types/FromComponent.types";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import "./TeamManageScreen.css";

type TeamItem = {
    id: number;
    name: string;
};

const toTeam = (item: Record<string, unknown>): TeamItem => ({
    id: Number(item.id ?? item.team_id ?? 0),
    name: String(item.name ?? item.team_name ?? ""),
});

const TeamManageScreen: React.FC = () => {
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();
    const { association } = useAuth();
    const associationId = String(association.id);

    const [teams, setTeams] = useState<TeamItem[]>([]);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadTeams = useCallback(async () => {
        const response = await TeamAPI.list();
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const mapped = (Array.isArray(response.data) ? response.data : [])
            .map((item: Record<string, unknown>) => toTeam(item))
            .filter((item) => item.id > 0);
        setTeams(mapped);
    }, [handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadTeams().finally(() => setLoadingStatus(false));
    }, [loadTeams, setLoadingStatus]);

    const handleCreateTeam = async (data: { [key: string]: string }) => {
        if (!data.name?.trim()) {
            return addNotification("Validation error", "Team name is required", "error");
        }

        setLoadingStatus(true);
        const response = await TeamAPI.create({ name: data.name.trim() }, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Created", "Team created successfully", "success");
        await loadTeams();
        setLoadingStatus(false);
    };

    const handleDeleteTeam = async (teamId: number) => {
        setLoadingStatus(true);
        const response = await TeamAPI.remove(teamId, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Deleted", "Team deleted successfully", "success");
        await loadTeams();
        setLoadingStatus(false);
    };

    const createFields: Field[] = [
        { name: "name", placeholder: "Team name", type: "text" },
    ];

    const columns: Column[] = [
        { header: "ID", accessor: "id" },
        { header: "Name", accessor: "name" },
    ];

    const buttons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faPen} title="Edit" />,
            bc: "green",
            placeholder: "Edit",
            action: { param: "id", function: (id: number) => nav(`/team/edit/${id}`) }
        },
        {
            button: <FontAwesomeIcon icon={faTrash} title="Delete" />,
            bc: "red",
            placeholder: "Delete",
            action: { param: "id", function: handleDeleteTeam }
        },
    ];

    if (loading) {
        return <LoadingScreenLocal />;
    }

    return (
        <div className="team-manage-screen">
            <TableComponent
                data={teams}
                columns={columns}
                buttons={buttons}
            />
            <FormComponent
                title="Create team"
                fields={createFields}
                onSubmit={handleCreateTeam}
                button={{ name: "Create" }}
            />
        </div>
    );
};

export default TeamManageScreen;
